/**
 * @fileoverview "Photo help this month" (D23): how much of the photo help's allowance a
 * gardener has used, as a bar, on the profile's Account group under
 * Subscription, and nowhere else.
 *
 * For subscribers, founders and trials (a gardener with no AI sees nothing).
 * The share used ("62% used") and, once most of it is used or all of it,
 * "Some comes back on {date}" (the server's: the first day the rolling 30
 * days drop back under the line as old uses age out). Never an amount of
 * money.
 *
 * Where: the profile (src/user/UserProfile.tsx), Account group, under
 * Subscription.
 * API: GET /ai-allowance (useAiAllowance('mine')).
 * Words (en/es): aiAllowance.barTitle ("Photo help this month"),
 * aiAllowance.barUsed, aiAllowance.barLabel, aiAllowance.barComesBack.
 * Watch out: the share is clamped to 0-100% for the bar, so an allowance
 * gone over still reads "100% used".
 *
 * @module PhotoHelpBar
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAiAllowance } from '../lib/useAiAllowance';

/**
 * The bar, or nothing for a gardener with no AI or before the answer arrives.
 *
 * @param props.className - Extra classes for the block.
 */
export const PhotoHelpBar: React.FC<{ className?: string }> = ({ className }) => {
  const { reactTranslate: t } = useTranslation();
  const { data: allowance } = useAiAllowance('mine');
  if (!allowance) return null;
  const percent = Math.round(Math.min(1, Math.max(0, allowance.share)) * 100);
  const comesBack = (allowance.near || allowance.over) && allowance.comesBack ? allowance.comesBack : null;
  return (
    <div className={cn('px-4 py-2.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[0.9375rem] font-medium">{t('aiAllowance.barTitle')}</span>
        <span className="text-sm text-muted-foreground">{t('aiAllowance.barUsed', { percent })}</span>
      </div>
      <div
        role="progressbar"
        aria-label={t('aiAllowance.barLabel')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
      </div>
      {comesBack && <p className="mt-1.5 text-sm text-muted-foreground">{t('aiAllowance.barComesBack', { date: comesBack })}</p>}
    </div>
  );
};

export default PhotoHelpBar;
